import {
  FirestoreDataConverter,
  QueryDocumentSnapshot,
  SnapshotOptions,
  Timestamp,
  WithFieldValue,
} from "firebase/firestore";
import {
  Task,
  TaskAutomationLevel,
  TaskChannel,
  TaskCompletionEmail,
  TaskCompletionText,
  TaskEvent,
  TaskStatus,
} from "../tasks/task";

const VALID_EVENTS: TaskEvent[] = [
  "birthday",
  "truck_anniversary",
  "lead_lost",
  "follow_up",
  "check_in",
  "custom",
];

const VALID_STATUSES: TaskStatus[] = ["pending", "completed", "cancelled"];

const VALID_CHANNELS: TaskChannel[] = ["email", "text"];

const VALID_AUTOMATION_LEVELS: TaskAutomationLevel[] = ["full", "partial", "manual"];

const parseChannels = (value: unknown): TaskChannel[] => {
  if (!Array.isArray(value)) return [];
  const out: TaskChannel[] = [];
  for (const entry of value) {
    if (typeof entry === "string" && (VALID_CHANNELS as string[]).includes(entry)) {
      const channel = entry as TaskChannel;
      if (!out.includes(channel)) out.push(channel);
    }
  }
  return out;
};

const parseAutomationLevel = (value: unknown): TaskAutomationLevel =>
  typeof value === "string" && (VALID_AUTOMATION_LEVELS as string[]).includes(value)
    ? (value as TaskAutomationLevel)
    : "partial";

const toIsoString = (value: unknown): string | null => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.toISOString();
  if (typeof value === "string") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
  }
  if (value instanceof Timestamp) return value.toDate().toISOString();
  return null;
};

const parseDate = (value: unknown): Date => {
  if (value instanceof Date) return value;
  if (value instanceof Timestamp) return value.toDate();
  if (typeof value === "string") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  }
  return new Date();
};

const parseDateNullable = (value: unknown): Date | null => {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (value instanceof Timestamp) return value.toDate();
  if (typeof value === "string") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const serializeCompletionEmail = (
  value: TaskCompletionEmail | null | undefined,
): Record<string, unknown> | null => {
  if (!value) return null;
  return {
    recipientEmail: value.recipientEmail || "",
    subject: value.subject || "",
    html: value.html || "",
    templateName: value.templateName || "",
    personalMessage: value.personalMessage || "",
    senderName: value.senderName ?? null,
    sentAt: toIsoString(value.sentAt) ?? new Date().toISOString(),
  };
};

const serializeCompletionText = (
  value: TaskCompletionText | null | undefined,
): Record<string, unknown> | null => {
  if (!value) return null;
  return {
    recipientPhone: value.recipientPhone || "",
    body: value.body || "",
    templateName: value.templateName || "",
    personalMessage: value.personalMessage || "",
    senderName: value.senderName ?? null,
    sentAt: toIsoString(value.sentAt) ?? new Date().toISOString(),
    messageDocId: value.messageDocId ?? null,
  };
};

const parseCompletionEmail = (value: unknown): TaskCompletionEmail | null => {
  if (!value || typeof value !== "object") return null;
  const data = value as Record<string, unknown>;
  return {
    recipientEmail: (data.recipientEmail as string) || "",
    subject: (data.subject as string) || "",
    html: (data.html as string) || "",
    templateName: (data.templateName as string) || "",
    personalMessage: (data.personalMessage as string) || "",
    senderName: (data.senderName as string | null | undefined) ?? null,
    sentAt: parseDate(data.sentAt),
  };
};

const parseCompletionText = (value: unknown): TaskCompletionText | null => {
  if (!value || typeof value !== "object") return null;
  const data = value as Record<string, unknown>;
  return {
    recipientPhone: (data.recipientPhone as string) || "",
    body: (data.body as string) || "",
    templateName: (data.templateName as string) || "",
    personalMessage: (data.personalMessage as string) || "",
    senderName: (data.senderName as string | null | undefined) ?? null,
    sentAt: parseDate(data.sentAt),
    messageDocId: (data.messageDocId as string | null | undefined) ?? null,
  };
};

export const taskConverter: FirestoreDataConverter<Task> = {
  toFirestore(task: WithFieldValue<Task>) {
    const { id, ...rest } = task as Task;
    return {
      ...rest,
      title: (task.title as string) || "",
      notes: (task.notes as string | null) ?? null,
      event: (task.event as TaskEvent) || "custom",
      status: (task.status as TaskStatus) || "pending",

      contact_id: (task.contact_id as string) || "",
      leadId: (task.leadId as string | null) ?? null,

      preferredChannels: parseChannels(task.preferredChannels),
      automationLevel: parseAutomationLevel(task.automationLevel),

      assignedToId:
        typeof task.assignedToId === "string" && task.assignedToId.trim()
          ? task.assignedToId.trim()
          : null,
      assignedToName:
        typeof task.assignedToName === "string" && task.assignedToName.trim()
          ? task.assignedToName.trim()
          : null,

      dueDate: toIsoString(task.dueDate),
      completedAt: toIsoString(task.completedAt),
      autoCompletedAt: toIsoString(task.autoCompletedAt),

      completionEmail: serializeCompletionEmail(
        task.completionEmail as TaskCompletionEmail | null | undefined,
      ),
      completionText: serializeCompletionText(
        task.completionText as TaskCompletionText | null | undefined,
      ),

      createdAt: toIsoString(task.createdAt) ?? new Date().toISOString(),
      updatedAt: toIsoString(task.updatedAt) ?? new Date().toISOString(),
      createdBy: (task.createdBy as string) || "",
    };
  },

  fromFirestore(snapshot: QueryDocumentSnapshot, options?: SnapshotOptions): Task {
    const data = snapshot.data(options);

    const event: TaskEvent = VALID_EVENTS.includes(data.event) ? data.event : "custom";
    const status: TaskStatus = VALID_STATUSES.includes(data.status) ? data.status : "pending";

    return {
      id: snapshot.id,
      title: data.title || "",
      notes: data.notes ?? null,
      event,
      status,

      contact_id: data.contact_id || "",
      leadId: data.leadId ?? null,

      preferredChannels: parseChannels(data.preferredChannels),
      automationLevel: parseAutomationLevel(data.automationLevel),

      assignedToId:
        typeof data.assignedToId === "string" && data.assignedToId.trim()
          ? data.assignedToId.trim()
          : null,
      assignedToName:
        typeof data.assignedToName === "string" && data.assignedToName.trim()
          ? data.assignedToName.trim()
          : null,

      dueDate: parseDate(data.dueDate),
      completedAt: parseDateNullable(data.completedAt),
      autoCompletedAt: parseDateNullable(data.autoCompletedAt),

      completionEmail: parseCompletionEmail(data.completionEmail),
      completionText: parseCompletionText(data.completionText),

      createdAt: parseDate(data.createdAt),
      updatedAt: parseDate(data.updatedAt),
      createdBy: data.createdBy || "",
    };
  },
};
